import type {
  DecisionSource,
  ReviewDecisionType,
  ReviewPhase,
  ReviewMode,
  ReviewStats,
} from '@/features/review/types';

interface ReviewSourceLike {
  source_id: string;
  current_phase?: string | null;
}

interface ReviewDecisionLike {
  source_id: string;
  decision: ReviewDecisionType;
  decision_source?: DecisionSource | null;
  phase?: ReviewPhase | null;
}

interface BuildReviewStatsOptions {
  reviewSources: ReviewSourceLike[];
  decisions: ReviewDecisionLike[];
  phase?: ReviewPhase;
  reviewMode?: ReviewMode;
  conflicts?: number;
  resolvedConflicts?: number;
}

export function isHumanDecisionSource(
  decisionSource?: DecisionSource | null,
): boolean {
  return decisionSource !== 'ai';
}

function isAuthoritativeDecisionSource(
  decisionSource: DecisionSource | null | undefined,
  reviewMode: ReviewMode,
): boolean {
  if (reviewMode === 'ai_only') {
    return true;
  }

  return isHumanDecisionSource(decisionSource);
}

function getMajorityDecision(
  counts: Map<ReviewDecisionType, number>,
): ReviewDecisionType | null {
  let topDecision: ReviewDecisionType | null = null;
  let topCount = 0;
  let isTie = false;

  for (const [decision, count] of counts.entries()) {
    if (count > topCount) {
      topDecision = decision;
      topCount = count;
      isTie = false;
      continue;
    }

    if (count === topCount && count > 0) {
      isTie = true;
    }
  }

  return isTie ? null : topDecision;
}

export function buildReviewStats({
  reviewSources,
  decisions,
  phase,
  reviewMode = 'ai_human',
  conflicts = 0,
  resolvedConflicts = 0,
}: BuildReviewStatsOptions): ReviewStats {
  const scopedSources = reviewSources.filter((reviewSource) =>
    phase ? reviewSource.current_phase === phase : true,
  );
  const totalSources = new Set(scopedSources.map((reviewSource) => reviewSource.source_id));

  const scopedHumanDecisions = decisions.filter(
    (decision) =>
      (!phase || decision.phase === phase) &&
      isAuthoritativeDecisionSource(decision.decision_source, reviewMode),
  );
  const screenedSources = new Set(
    scopedHumanDecisions.map((decision) => decision.source_id),
  );

  const decisionCountsBySource = new Map<string, Map<ReviewDecisionType, number>>();
  for (const decision of scopedHumanDecisions) {
    if (!decisionCountsBySource.has(decision.source_id)) {
      decisionCountsBySource.set(decision.source_id, new Map());
    }

    const counts = decisionCountsBySource.get(decision.source_id)!;
    counts.set(decision.decision, (counts.get(decision.decision) ?? 0) + 1);
  }

  let included = 0;
  let excluded = 0;
  let maybe = 0;

  for (const counts of decisionCountsBySource.values()) {
    const topDecision = getMajorityDecision(counts);

    if (topDecision === 'include') included++;
    if (topDecision === 'exclude') excluded++;
    if (topDecision === 'maybe') maybe++;
  }

  return {
    total_sources: totalSources.size,
    screened: screenedSources.size,
    included,
    excluded,
    maybe,
    conflicts,
    resolved_conflicts: resolvedConflicts,
  };
}
